import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useFetchAllVendorsLazyQuery, Vendor } from '../../../generated'
import { GRAPHQL_QUERY_POLICY, PAGE_LIMIT } from '../../constants'
import { useDebounce } from 'use-debounce'
import { Autocomplete, Box, TextField } from '@mui/material'
import { Controller, useFormContext } from 'react-hook-form'
import { AutoCompleteOption } from '../../interfaceTypes/common'
import { AutoCompleteWithGlAccount } from '../../interfaceTypes/forms'

const VendorAutoComplete = ({
  currentVendor,
  parent,
  name,
  label,
  errorMessage,
  sx = { paddingTop: 1 },
  customOnChange,
  setGlAccount,
  autoFocus,
  autoSelectOnTab = false
}: {
  currentVendor?: Vendor
  parent?: Vendor
  name: string
  label: string
  sx?: object
  errorMessage?: string
  customOnChange?: (option: AutoCompleteOption | null) => void
  setGlAccount?: boolean
  autoFocus?: boolean
  /**
   * Keep the closest match highlighted while typing and commit it on Tab/Enter, the way the Bank
   * Account and GL Account dropdowns behave. Typing either the vendor name or its ID narrows the
   * list, since the server searches both fields.
   *
   * Note this defaults to false where the sibling OfficeAutoComplete and AccountDropdown default
   * to true — not a considered difference in behaviour, just a narrow rollout: the other three
   * screens using this component were left as they were, so each opts in on its own.
   */
  autoSelectOnTab?: boolean
}) => {
  const [vendors, setVendors] = useState<AutoCompleteWithGlAccount[]>([])
  const [searchTerm, setSearchTerm] = useState<string>('')
  const [page, setPage] = useState<number>(1)
  const [selectedVendor, setSelectedVendor] = useState<AutoCompleteWithGlAccount | null>(
    parent
      ? { label: parent.name, value: parent.id }
      : currentVendor
      ? {
          label: `(#${currentVendor.vendorId}) ${currentVendor.name}`,
          value: currentVendor.id,
          glAccount: currentVendor.glAccount
            ? { label: currentVendor.glAccount.name || '', value: currentVendor.glAccount.id || '' }
            : undefined
        }
      : null
  )
  const [debouncedSearchTerm] = useDebounce(searchTerm, 500)
  // See the autoHighlight/autoSelect props below -- true only once the user has actually typed.
  const [hasTypedFilter, setHasTypedFilter] = useState(false)

  const { control, setValue } = useFormContext()

  const [fetchVendors, { loading }] = useFetchAllVendorsLazyQuery({
    ...GRAPHQL_QUERY_POLICY,
    variables: undefined,
    onCompleted(data) {
      if (data?.fetchAllVendors?.vendors?.length) {
        const newVendors = data.fetchAllVendors.vendors
          .filter((vendor): vendor is Vendor => {
            return vendor !== null
            // && vendor.id !== currentVendor?.id
          })
          .map(vendor => ({
            value: vendor?.id || 0,
            label: `(#${vendor.vendorId}) ${vendor?.name || ''}\nAddress: ${vendor?.addresses?.[0]?.address1 || 'N/A'}`,
            glAccount: {
              label: vendor.glAccount?.name || '',
              value: vendor.glAccount?.id || ''
            },
            office: {
              label: vendor.office?.name || '',
              value: vendor.office?.id || 0
            }
          }))
        if (page === 1) setVendors(newVendors)
        else setVendors(prev => [...prev, ...newVendors])
      }
    }
  })

  useEffect(() => {
    setPage(1)
  }, [searchTerm])

  useEffect(() => {
    fetchVendors({
      variables: {
        fetchVendorsInput: {
          keyword: debouncedSearchTerm,
          paginationOptions: {
            page,
            limit: PAGE_LIMIT
          }
        }
      }
    })
  }, [debouncedSearchTerm, fetchVendors, page])

  const handleAccountChange = (option: AutoCompleteWithGlAccount | null) => {
    setSelectedVendor(option)
    customOnChange?.(option)
    if (setGlAccount) setValue('glAccount', option?.glAccount ? { ...option?.glAccount } : null)
  }
  const inputRef = useRef<HTMLInputElement>(null)

  // Focus on the next tick rather than through TextField's `autoFocus`. Inside a MUI Dialog the
  // focus trap claims focus right after mount, and under React 18 StrictMode its effect runs
  // twice — the cleanup hands focus back to the trigger, so the second run pulls it onto the
  // dialog root and a plain `autoFocus` is lost. Focusing after that settles works in both modes.
  useEffect(() => {
    if (!autoFocus) return
    const timeout = window.setTimeout(() => inputRef.current?.focus(), 0)
    return () => window.clearTimeout(timeout)
  }, [autoFocus])

  const handleScroll = useCallback(
    (event: React.SyntheticEvent) => {
      const listboxNode = event.currentTarget
      if (listboxNode.scrollTop + listboxNode.clientHeight >= listboxNode.scrollHeight - 5 && !loading) {
        setPage(prev => prev + 1)
      }
    },
    [loading]
  )

  return (
    <Controller
      name={name}
      control={control}
      render={({ field }) => (
        <Autocomplete
          sx={{ ...sx, '& .MuiInputBase-input': { fontSize: '0.8125rem' } }}
          options={vendors}
          value={selectedVendor}
          loading={loading}
          onChange={(_, value) => {
            field.onChange(value)
            return handleAccountChange(value)
          }}
          onInputChange={(_, newInputValue, reason) => {
            setSearchTerm(newInputValue)
            // `reason` separates a real keystroke from MUI echoing the label back after a
            // selection ('reset') or a clear ('clear'), which must not count as typing.
            setHasTypedFilter(reason === 'input' && !!newInputValue.trim())
          }}
          onBlur={() => setHasTypedFilter(false)}
          getOptionLabel={option =>
            typeof option === 'string' ? option : String(option.label).split('\n')[0]
          }
          renderOption={(props, option) => (
            <li {...props}>
              <Box sx={{ whiteSpace: 'pre-line', py: 0.5, textAlign: 'left' }}>{option.label}</Box>
            </li>
          )}
          isOptionEqualToValue={(option, value) => option.value === value.value}
          // Armed only while a typed filter is standing: `autoSelect` commits the highlighted
          // option on *any* blur, so leaving it always on means clicking the field and clicking
          // away silently assigns whichever vendor happened to be first in the list.
          autoHighlight={autoSelectOnTab && hasTypedFilter}
          autoSelect={autoSelectOnTab && hasTypedFilter}
          renderInput={params => (
            <TextField
              {...params}
              label={label}
              inputRef={inputRef}
              error={!!errorMessage}
              helperText={errorMessage}
            />
          )}
          ListboxProps={{
            role: 'list-box',
            onScroll: handleScroll
          }}
          noOptionsText={loading ? 'Loading...' : 'No Vendors!'}
        />
      )}
    />
  )
}

export default VendorAutoComplete
